import { Suspense, lazy } from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import Link from 'next/link'

import Button from '../../../../app/components/Button'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import { showAuthModal } from '../../../../auth/store/authSlice'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import {
  authCustomerIsLoggedInSelector,
  authInitializedSelector,
} from '../../../../auth/store/authSelectors'
import ClientAuthContainer from '../../../../auth/components/ClientAuthContainer'
import Skeleton from '../../../../app/components/Skeleton'
import useRouteMatch from '../../../../app/hooks/useRouteMatch'
import { AppRoutes, AppTestIds } from '../../../../app/constants'
import FillYourInfoModal from '../../../../auth/components/FillYourInfoModal'
import { AuthModalType } from '../../../../auth/types'
import useDeviceSize from '../../../../app/hooks/useDeviceSize'
import AuthSignInURLFilter from '../../../../auth/components/AuthSignInURLFilter'
import { preOrderUserSelector } from '../../../../preOrder/store/preOrderSelectors'
import AuthSignUpURLFilter from '../../../../auth/components/AuthSignUpURLFilter'
import isAppWhiteLabeled from '../../../../app/utils/isAppWhiteLabeled'
import useIsGiftCardPreviewRoute from '../../../../giftCards/hooks/useIsGiftCardPreviewRoute'
import { clientContextIsAnonymousCustomerSelector } from '../../../../clientContext/store/clientContextSelectors'

import NavbarUserProfile from './NavbarUserProfile'
import NavLanguageSwitcher from './NavLanguageSwitcher'
import CartButton from './CartButton'
import styles from './NavActions.module.scss'

const AuthModal = lazy(() => import('../../../../auth/components/authModal'))
const AuthProfileSelectionModal = lazy(
  () =>
    import('../../../../auth/components/profileSelection/AuthProfileSelectionModal'),
)
const CompanyMemberVerificationPendingModal = lazy(
  () =>
    import('../../../../company/components/CompanyMemberVerificationPendingModal'),
)
const CompanySignUpModal = lazy(
  () => import('../../../../auth/components/companySignUp/CompanySignUpModal'),
)
const ReviewAccountDetailsModal = lazy(
  () =>
    import('../../../../auth/components/reviewAccount/ReviewAccountDetailsModal'),
)

export default function NavActions() {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()

  const isWhiteLabeled = isAppWhiteLabeled()

  const isLoggedIn = useAppSelector(authCustomerIsLoggedInSelector)
  const isAuthInitialized = useAppSelector(authInitializedSelector)
  const shouldHideCartButton = useRouteMatch([
    AppRoutes.Checkout,
    AppRoutes.PreOrderLink,
    AppRoutes.GroupOrder,
    AppRoutes.QROrderStatus,
  ])
  const { isMobile } = useDeviceSize()
  const preOrderUser = useAppSelector(preOrderUserSelector)
  const isAnonymousCustomer = useAppSelector(
    clientContextIsAnonymousCustomerSelector,
  )

  const isAuthRoute = useRouteMatch([AppRoutes.Auth])
  const isGiftCardPreviewRoute = useIsGiftCardPreviewRoute()

  const isNavActionsEligibleRoute = !isAuthRoute && !isGiftCardPreviewRoute

  const isPreOrderStartRoute = useRouteMatch([
    AppRoutes.PreOrderLink,
    AppRoutes.GroupOrder,
  ])

  const isShowProfile = !!isLoggedIn || !!preOrderUser
  // Table bookings link to the guest's profile, so signed-in guests only
  const isShowMyVisits = !!isLoggedIn && !isAnonymousCustomer
  // Prevent showing sign-in button when isLoggedIn = null
  const isShowSignIn =
    isLoggedIn === false && !preOrderUser && !isPreOrderStartRoute

  function handleSignInClick() {
    dispatch(showAuthModal(AuthModalType.SignIn))
  }

  return (
    <>
      {isNavActionsEligibleRoute && (
        <div className={styles.actionButtonContainer}>
          {!shouldHideCartButton && (
            <ClientAuthContainer
              skeleton={<Skeleton className={styles.cartBtnSkeleton} />}
            >
              <CartButton />
            </ClientAuthContainer>
          )}
          {!isMobile && (
            <>
              {isShowMyVisits && (
                <Button
                  size="large"
                  variant="secondary"
                  component={Link}
                  href={AppRoutes.Visits}
                  className={styles.myVisitsButton}
                  data-testid={AppTestIds.NavBar.Button.MyVisits}
                >
                  {t('components.navBar.myVisits')}
                </Button>
              )}
              {isShowProfile && <NavbarUserProfile />}
              {isShowSignIn && (
                <Button
                  size="large"
                  variant="tertiary"
                  onClick={handleSignInClick}
                  disabled={!isAuthInitialized}
                  className={clsx(styles.signInButton, {
                    [styles.whiteLabeled]: isWhiteLabeled,
                  })}
                  data-testid={AppTestIds.NavBar.Button.SignIn}
                >
                  {t('components.navBar.signIn')}
                </Button>
              )}
            </>
          )}
          <ClientAuthContainer fallbackSpinning={false}>
            <Suspense>
              <AuthModal />
              <FillYourInfoModal />
              <AuthProfileSelectionModal />
              <CompanyMemberVerificationPendingModal />
              <AuthSignInURLFilter />
              <AuthSignUpURLFilter />
              {isWhiteLabeled && (
                <>
                  <CompanySignUpModal />
                  <ReviewAccountDetailsModal />
                </>
              )}
            </Suspense>
          </ClientAuthContainer>
        </div>
      )}
      <NavLanguageSwitcher />
    </>
  )
}
